import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { brokerRoutes, ConfigurationModels, PaymentModels } from '@wz-models';
import { BaseNavigationService } from '@wz-services';

@Injectable({
  providedIn: 'root',
})
export class TunnelNavigationService extends BaseNavigationService {
  constructor(router: Router, config: ConfigurationModels.Configuration) {
    super(router, config);
  }

  public getUrlFromKey(key: string): any {
    return brokerRoutes[key as keyof typeof brokerRoutes];
  }

  goToRegister(): Promise<boolean> {
    let registerUrl = brokerRoutes.REGISTER;
    return this.router.navigate([registerUrl]);
  }

  goToSubscription(): Promise<boolean> {
    let subscriptionUrl = brokerRoutes.SUBSCRIPTION;
    return this.router.navigate([subscriptionUrl]);
  }

  goToDashboard(): Promise<boolean> {
    let dashboardUrl = brokerRoutes.APP_DASHBOARD;
    return this.router.navigate([dashboardUrl]);
  }

  goToClientDetails(clientId: number, policyId: number) {
    return this.router.navigate([
      brokerRoutes.APP_CLIENTS_DETAILS_INFOS.format(clientId, policyId),
    ]);
  }

  goToClientDocuments(clientId: number, policyId: number) {
    return this.router.navigate([
      brokerRoutes.APP_CLIENTS_DETAILS_DOCUMENTS.format(clientId, policyId),
    ]);
  }

  goToPayClientComplement(
    clientId: number,
    policyId: number,
    complementId: number,
  ) {
    return this.router.navigate([
      brokerRoutes.APP_CLIENTS_DETAILS_PAY_COMPLEMENT.format(
        clientId,
        policyId,
        complementId,
      ),
    ]);
  }

  goToPayClientReceipts(clientId: number, policyId: number) {
    return this.router.navigate([
      brokerRoutes.APP_CLIENTS_DETAILS_PAY_RECEIPTS.format(clientId, policyId),
    ]);
  }

  goToLogin(): Promise<boolean> {
    let loginUrl = brokerRoutes.LOGIN;
    return this.router.navigate([loginUrl]);
  }

  goToSignature(): Promise<boolean> {
    let etape1Url = brokerRoutes.SIGNATURE_ETAPE1;
    return this.router.navigate([etape1Url]);
  }

  goToSignatureEtape2(): Promise<boolean> {
    let etape2Url = brokerRoutes.SIGNATURE_ETAPE2;
    return this.router.navigate([etape2Url]);
  }

  goToCarInsuranceQuotation(): any {
    return this.router.navigate([brokerRoutes.APP_CAR_INSURANCE_QUOTATION]);
  }

  goToSouscriptionEnligne(): any {
    return this.router.navigate([
      'souscription-en-ligne',
      {
        GUID: '5da929f3-bb93-4ba2-ad4a-1f9ff6407e09',
        MDP: 'F31125EE65612300DFA37E3B4AA77917',
      },
    ]);
  }
  goToTunnelWithParams(): any {
    let url = localStorage.getItem('urlNext')?.toString();
    return this.router.navigate([url]);
  }

  isUrlParamOrigin(urlNext: any): any {
    let isUrlParamOrigin = false;
    const regexpWithoutE = /[/]demande-devis[/](.*)/g;
    const matchParamOrigin = urlNext.match(regexpWithoutE);

    if (matchParamOrigin && matchParamOrigin.length > 0) {
      let urlP = matchParamOrigin[0].split('/')[0];
      if (urlP != 'devis' && urlP != 'tarif') {
        isUrlParamOrigin = true;
      }
    }

    return isUrlParamOrigin;
  }

  getUrlParamOrigin(urlNext: any): any {
    const regexpWithoutE = /[/]demande-devis[/](.*)/g;
    const matchParamOrigin = urlNext.match(regexpWithoutE);

    if (matchParamOrigin && matchParamOrigin.length > 0) {
      let urlP = matchParamOrigin[0].split('/')[0];
      if (urlP != 'devis' && urlP != 'tarif') {
        return matchParamOrigin[0];
      }
    }

    return false;
  }

  goToUrlParamOrigin(url: any): any {
    return this.router.navigate([url]);
  }

  goToCarInsurancePrices(): any {
    return this.router.navigate([brokerRoutes.APP_CAR_INSURANCE_PRICES]);
  }

  goToQuotationList() {
    return this.router.navigate([brokerRoutes.APP_QUOTATION_LIST]);
  }

  goToRequestList() {
    return this.router.navigate(['/app/board/requests']);
  }

  goToInstanceList() {
    return this.router.navigate(['/app/board/instances']);
  }

  goToDocTohandle() {
    return this.router.navigate(['/app/board/documentsTohandle']);
  }

  goToDocTohandleHealth() {
    return this.router.navigate(['/app/board/documentsTohandleHealth']);
  }

  goToCarInsuranceSubscription(publicId: string): any {
    return this.router.navigate([
      brokerRoutes.APP_CAR_INSURANCE_SUBSCRIPTION.format(publicId),
    ]);
  }

  goToCarInsurancePayment(publicId: string) {
    return this.router.navigate([
      brokerRoutes.APP_CAR_INSURANCE_PAYMENT.format(publicId),
    ]);
  }

  goToCarInsuranceConfirmation(publicId: string) {
    return this.router.navigate([
      brokerRoutes.APP_CAR_INSURANCE_CONFIRMATION.format(publicId),
    ]);
  }

  carInsurancePaymentBackFromPayzenUrl(
    publicId: string,
    status: PaymentModels.FromPayzenStatus,
  ) {
    const base = this.config.applicationPaths.appAfiUrl + '/#';
    if (status === PaymentModels.FromPayzenStatus.Success) {
      return `${base}${brokerRoutes.APP_CAR_INSURANCE_CONFIRMATION.format(publicId)}`;
    }
    return `${base}${brokerRoutes.APP_CAR_INSURANCE_SUBSCRIPTION.format(publicId)}?backFromPayzen=${status}`;
  }

  getAvenantPaymentRequest(
    clientId: number,
    policyId: number,
  ): PaymentModels.PaymentRequest {
    const base = this.config.applicationPaths.appAfiUrl + '/#';
    const backFromPz = (status: PaymentModels.FromPayzenStatus) =>
      `${base}${brokerRoutes.APP_CLIENTS_DETAILS_INFOS.format(clientId, policyId)}?backFromPayzen=${status}`;

    return {
      cancelUrl: backFromPz(PaymentModels.FromPayzenStatus.Cancelled),
      errorUrl: backFromPz(PaymentModels.FromPayzenStatus.Error),
      refusedUrl: backFromPz(PaymentModels.FromPayzenStatus.Refused),
      successUrl: `${base}${brokerRoutes.APP_CLIENTS_DETAILS_PAY_AVENANT_CONFIRM.format(clientId, policyId)}`,
    };
  }

  getComplementPaymentRequest(
    clientId: number,
    policyId: number,
    complementId: number,
  ): PaymentModels.PaymentRequest {
    const base = this.config.applicationPaths.appAfiUrl + '/#';
    const backFromPz = (status: PaymentModels.FromPayzenStatus) =>
      `${base}${brokerRoutes.APP_CLIENTS_DETAILS_PAY_COMPLEMENT.format(
        clientId,
        policyId,
        complementId,
      )}?backFromPayzen=${status}`;

    return {
      cancelUrl: backFromPz(PaymentModels.FromPayzenStatus.Cancelled),
      errorUrl: backFromPz(PaymentModels.FromPayzenStatus.Error),
      refusedUrl: backFromPz(PaymentModels.FromPayzenStatus.Refused),
      successUrl: `${base}${brokerRoutes.APP_CLIENTS_DETAILS_PAY_COMPLEMENT_CONFIRM.format(
        clientId,
        policyId,
        complementId,
      )}`,
    };
  }

  getReceiptsPaymentRequest(
    clientId: number,
    policyId: number,
  ): PaymentModels.PaymentRequest {
    const base = this.config.applicationPaths.appAfiUrl + '/#';
    const backFromPz = (status: PaymentModels.FromPayzenStatus) =>
      `${base}${brokerRoutes.APP_CLIENTS_DETAILS_PAY_RECEIPTS.format(clientId, policyId)}?backFromPayzen=${status}`;

    return {
      cancelUrl: backFromPz(PaymentModels.FromPayzenStatus.Cancelled),
      errorUrl: backFromPz(PaymentModels.FromPayzenStatus.Error),
      refusedUrl: backFromPz(PaymentModels.FromPayzenStatus.Refused),
      successUrl: `${base}${brokerRoutes.APP_CLIENTS_DETAILS_PAY_RECEIPTS_CONFIRM.format(clientId, policyId)}`,
    };
  }

  getQuotationPaymentRequest(
    quotationId: string,
  ): PaymentModels.PaymentRequest {
    return {
      cancelUrl: this.carInsurancePaymentBackFromPayzenUrl(
        quotationId,
        PaymentModels.FromPayzenStatus.Cancelled,
      ),
      errorUrl: this.carInsurancePaymentBackFromPayzenUrl(
        quotationId,
        PaymentModels.FromPayzenStatus.Error,
      ),
      refusedUrl: this.carInsurancePaymentBackFromPayzenUrl(
        quotationId,
        PaymentModels.FromPayzenStatus.Refused,
      ),
      successUrl: this.carInsurancePaymentBackFromPayzenUrl(
        quotationId,
        PaymentModels.FromPayzenStatus.Success,
      ),
    };
  }

  getAfterComplementPaymentRedirect(clientId: number, policyId: number) {
    return brokerRoutes.APP_CLIENTS_DETAILS_INFOS.format(clientId, policyId);
  }

  getAfterReceiptsPaymentRedirect(clientId: number, policyId: number) {
    return brokerRoutes.APP_CLIENTS_DETAILS_INFOS.format(clientId, policyId);
  }

  getAfterAvenantPaymentRedirect(
    clientId: number,
    policyId: number,
    requestPublicId: string,
  ): string {
    return brokerRoutes.APP_CLIENTS_DETAILS_INFOS_WITH_REQUEST.format(
      clientId,
      policyId,
      requestPublicId,
    );
  }

  goToHealthInsuranceSubscription(publicId: string, packageName: string): any {
    return this.router.navigate(
      [
        brokerRoutes.APP_HEALTH_INSURANCE_SUBSCRIPTION.format(
          packageName,
          publicId,
        ),
      ],
      {
        preserveFragment: true,
        queryParamsHandling: 'preserve',
      },
    );
  }

  goToHealthInsuranceConfirmation(publicId: string, packageName: string): any {
    return this.router.navigate(
      [
        brokerRoutes.APP_HEALTH_INSURANCE_CONFIRMATION.format(
          packageName,
          publicId,
        ),
      ],
      {
        preserveFragment: true,
        queryParamsHandling: 'preserve',
      },
    );
  }

  goToRopSubscription(publicId: string): any {
    return this.router.navigate(
      [brokerRoutes.APP_ROP_INSURANCE_SUBSCRIPTION.format(publicId)],
      {
        preserveFragment: true,
        queryParamsHandling: 'preserve',
      },
    );
  }

  goToRopConfirmation(publicId: string): any {
    return this.router.navigate(
      [brokerRoutes.APP_ROP_INSURANCE_CONFIRMATION.format(publicId)],
      {
        preserveFragment: true,
        queryParamsHandling: 'preserve',
      },
    );
  }
}
